import React, { ComponentType, createElement } from "react";

import { Feature } from "@formant/core/model/Feature";
import { ResourcePolicy } from "@formant/core/model/Policies";
import { Resource } from "@formant/core/model/Resource";

import { CheckAuthentication } from "../component/CheckAuthentication";
import { CheckTermsAcceptance } from "../component/CheckTermsAcceptance";
import { RestrictedComponent } from "../component/common/RestrictedComponent";
import { FeatureNotAvailable } from "../component/FeatureNotAvailable";
import { Paywall } from "../component/Paywall";
import { StandardLayout } from "../component/StandardLayout";
import { IRoute, RoutePattern, RouteProps } from "./IRoute";
import { Route } from "./Route";

export type RouteFn<
    TProps extends RouteProps,
    TRoute extends IRoute<TProps>,
> = (component: ComponentType<TProps>) => TRoute;

const defaultRouteFn =
    <TProps extends RouteProps>(pattern: RoutePattern) =>
    (component: ComponentType<TProps>) =>
        new Route(pattern, component);

export function AuthRoute<
    TProps extends RouteProps = Record<string, never>,
    TRoute extends IRoute<TProps> = IRoute<TProps>,
>(
    route: RoutePattern | RouteFn<TProps, TRoute>,
    component: ComponentType<TProps>,
    feature?: Feature,
): IRoute<TProps> {
    const routeFn =
        typeof route === "function" ? route : defaultRouteFn<TProps>(route);
    return routeFn((props: TProps) => (
        <CheckAuthentication>
            <CheckTermsAcceptance>
                {feature ? (
                    <Paywall feature={feature} ErrorPage={FeatureNotAvailable}>
                        {createElement(component, props)}
                    </Paywall>
                ) : (
                    createElement(component, props)
                )}
            </CheckTermsAcceptance>
        </CheckAuthentication>
    ));
}

export function DefaultRoute<
    TProps extends RouteProps = Record<string, never>,
    TRoute extends IRoute<TProps> = IRoute<TProps>,
>(
    route: RoutePattern | RouteFn<TProps, TRoute>,
    component: ComponentType<TProps>,
    feature?: Feature,
): IRoute<TProps> {
    return AuthRoute(
        route,
        (props: TProps) => (
            <StandardLayout>{createElement(component, props)}</StandardLayout>
        ),
        feature,
    );
}

export function RestrictedRoute<
    TProps extends RouteProps = Record<string, never>,
    TRoute extends IRoute<TProps> = IRoute<TProps>,
    T extends Resource = Resource,
>(
    route: RoutePattern | RouteFn<TProps, TRoute>,
    component: ComponentType<TProps>,
    resource: Resource,
    requiredAccess: ResourcePolicy<T>,
    requiredFeature?: Feature,
): IRoute<TProps> {
    return DefaultRoute(
        route,
        (props: TProps) => (
            <RestrictedComponent
                resource={resource}
                requiredAccess={requiredAccess}
                requiredFeature={requiredFeature}
            >
                {createElement(component, props)}
            </RestrictedComponent>
        ),
        requiredFeature,
    );
}
